import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils'

const SidebarTooltipProvider = TooltipPrimitive.Provider
const SidebarTooltipRoot = TooltipPrimitive.Root
const SidebarTooltipTrigger = TooltipPrimitive.Trigger

const SidebarTooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 8, align = 'center', ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      align={align}
      collisionPadding={{ top: 8, right: 8, bottom: 8, left: 8 }}
      className={cn(
        'z-[9999] overflow-hidden rounded-md px-3 py-1.5 text-sm font-medium shadow-md',
        'animate-in fade-in-0 zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2',
        'data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2',
        'data-[side=top]:slide-in-from-bottom-2',
        // Estilo BrainHub - verde esmeralda
        'bg-emerald-50 text-emerald-900 border border-emerald-200',
        'dark:bg-emerald-950 dark:text-emerald-100 dark:border-emerald-800',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
SidebarTooltipContent.displayName = TooltipPrimitive.Content.displayName

interface SidebarTooltipProps {
  children: React.ReactNode
  content: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  delayDuration?: number
}

export function SidebarTooltip({
  children,
  content,
  side = 'right',
  delayDuration = 0,
}: SidebarTooltipProps) {
  return (
    <SidebarTooltipRoot delayDuration={delayDuration}>
      <SidebarTooltipTrigger asChild>{children}</SidebarTooltipTrigger>
      <SidebarTooltipContent side={side}>{content}</SidebarTooltipContent>
    </SidebarTooltipRoot>
  )
}

export {
  SidebarTooltipProvider,
  SidebarTooltipRoot,
  SidebarTooltipTrigger,
  SidebarTooltipContent,
}
